import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Spinner,
  WarningCircleIcon,
} from '@noor/ui';

interface AcquireChatDialogProps {
  open: boolean;
  pending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

/**
 * "Are you sure you want to reply?" — shown when an operator answers a chat
 * nobody owns yet (cpanel `ConfirmDialog` in `ChatMessageInput`). Confirming
 * makes them the responsible operator and then sends the draft. Focus lands on
 * "Yes" so a second Enter confirms, like cpanel's Enter shortcut.
 */
export function AcquireChatDialog({
  open,
  pending,
  onOpenChange,
  onConfirm,
}: AcquireChatDialogProps) {
  const { t } = useTranslation('inbox');
  const confirmRef = useRef<HTMLButtonElement>(null);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => (pending ? undefined : onOpenChange(next))}
    >
      <DialogContent
        showClose={false}
        className="max-w-[calc(100%-2rem)] gap-8 rounded-3xl p-6 sm:max-w-[30rem]"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          confirmRef.current?.focus();
        }}
      >
        <div className="flex w-full flex-col items-center gap-6 px-[42px]">
          <WarningCircleIcon className="size-[72px] shrink-0" />
          <div className="flex w-full flex-col items-center gap-2 text-center">
            <DialogTitle className="text-[22px] leading-tight font-semibold tracking-[-0.88px]">
              {t('acquire.title')}
            </DialogTitle>
            <DialogDescription className="text-sm leading-snug font-medium text-muted-foreground">
              {t('acquire.description')}
            </DialogDescription>
          </div>
        </div>
        <div className="flex w-full gap-2">
          <Button
            type="button"
            variant="soft"
            shape="rounded"
            size="modal"
            className="flex-1"
            disabled={pending}
            onClick={() => onOpenChange(false)}
          >
            {t('acquire.no')}
          </Button>
          <Button
            ref={confirmRef}
            type="button"
            variant="dark"
            shape="rounded"
            size="modal"
            className="flex-1"
            disabled={pending}
            aria-busy={pending}
            onClick={onConfirm}
          >
            {pending ? <Spinner size="sm" className="text-current" /> : null}
            {t('acquire.yes')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
